import { useEffect, useState, useCallback } from "react";
import { API_BASE } from "../../utils/api";
import { getLawdCd } from "../../utils/lawdCdMap";
import { formatPrice } from "../../utils/format";

interface ChartPoint {
  ym: string;
  label: string;
  avg: number | null;
  count: number;
}

interface Trade {
  date: string;
  jimo: string;
  address: string;
  area: number;
  pyeong: number;
  price: number;
  pricePerPyeong: number;
}

interface Props {
  sido: string;
  sigungu: string;
  isMobile: boolean;
}

// ── SVG 라인 차트 ──────────────────────────────────────
function PriceChart({ data, isMobile }: { data: ChartPoint[]; isMobile: boolean }) {
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);

  const validPrices = data
    .map((d) => d.avg)
    .filter((v): v is number => v !== null);

  if (validPrices.length === 0) {
    return (
      <div style={st.noData}>{"이 기간 거래 데이터가 없습니다"}</div>
    );
  }

  const W = 560, H = 190;
  const PL = isMobile ? 54 : 64;
  const PR = 12, PT = 14, PB = 38;
  const cW = W - PL - PR;
  const cH = H - PT - PB;

  const maxP = Math.max(...validPrices);
  const minP = Math.min(...validPrices);
  const rng = maxP - minP || maxP * 0.2 || 10000;
  const dMax = maxP + rng * 0.15;
  const dMin = Math.max(0, minP - rng * 0.15);
  const dRng = dMax - dMin || 1;

  const getX = (i: number) =>
    PL + (data.length < 2 ? cW / 2 : (i / (data.length - 1)) * cW);
  const getY = (v: number) => PT + cH - ((v - dMin) / dRng) * cH;

  // 경로 생성 (null 건너뜀)
  let path = "";
  let prevVal = false;
  data.forEach((d, i) => {
    if (d.avg === null) { prevVal = false; return; }
    const x = getX(i), y = getY(d.avg);
    path += prevVal ? `L ${x.toFixed(1)} ${y.toFixed(1)} ` : `M ${x.toFixed(1)} ${y.toFixed(1)} `;
    prevVal = true;
  });

  // Y축 눈금
  const gridLines = 4;
  const grids = Array.from({ length: gridLines + 1 }, (_, i) => ({
    y: PT + (cH * (gridLines - i)) / gridLines,
    v: dMin + (dRng * i) / gridLines,
  }));

  const fmtY = (v: number) =>
    v >= 10000 ? `${(v / 10000).toFixed(1)}억` : `${Math.round(v / 1000)}천`;

  const hoverData = hoverIdx !== null ? data[hoverIdx] : null;

  return (
    <div style={{ position: "relative", userSelect: "none" }}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        style={{ width: "100%", display: "block" }}
        onMouseLeave={() => setHoverIdx(null)}
      >
        {/* 배경 그리드 */}
        {grids.map(({ y, v }, i) => (
          <g key={i}>
            <line
              x1={PL} x2={W - PR} y1={y} y2={y}
              stroke="var(--border-light)" strokeWidth="1"
            />
            <text
              x={PL - 4} y={y + 3.5}
              textAnchor="end" fontSize="10" fill="var(--text-muted)"
            >
              {fmtY(v)}
            </text>
          </g>
        ))}

        {/* 영역 채우기 (반투명 골드) */}
        {validPrices.length > 1 && (() => {
          let firstIdx = -1, lastIdx = -1;
          for (let i = 0; i < data.length; i++) { if (data[i].avg !== null) { if (firstIdx === -1) firstIdx = i; lastIdx = i; } }
          if (firstIdx === -1) return null;
          return (
            <path
              d={path + ` L ${getX(lastIdx).toFixed(1)} ${PT + cH} L ${getX(firstIdx).toFixed(1)} ${PT + cH} Z`}
              fill="rgba(212,168,83,0.08)"
              stroke="none"
            />
          );
        })()}

        {/* 라인 */}
        <path d={path} stroke="#D4A853" strokeWidth="2.5" fill="none" strokeLinejoin="round" strokeLinecap="round" />

        {/* 데이터 포인트 + X 레이블 */}
        {data.map((d, i) => {
          const x = getX(i);
          const isHover = hoverIdx === i;
          return (
            <g key={d.ym}>
              {/* X 레이블 */}
              <text
                x={x} y={H - 6}
                textAnchor="middle" fontSize={isMobile ? "8" : "9"}
                fill={isHover ? "var(--lt-gold)" : "var(--text-muted)"}
              >
                {d.label}
              </text>
              {/* 마우스 반응 영역 (투명) */}
              <rect
                x={x - 14} y={PT} width="28" height={cH + 10}
                fill="transparent"
                style={{ cursor: "crosshair" }}
                onMouseEnter={() => setHoverIdx(i)}
              />
              {/* 데이터 있을 때만 점 표시 */}
              {d.avg !== null && (
                <circle
                  cx={x} cy={getY(d.avg)}
                  r={isHover ? 5.5 : 3.5}
                  fill={isHover ? "#B8972E" : "#D4A853"}
                  stroke="var(--bg-card)" strokeWidth="2"
                  style={{ transition: "r 0.1s" }}
                />
              )}
            </g>
          );
        })}
      </svg>

      {/* 툴팁 */}
      {hoverData && hoverData.avg !== null && (
        <div style={{
          position: "absolute",
          top: `${((getY(hoverData.avg)) / H * 100).toFixed(1)}%`,
          left: `${(getX(hoverIdx!) / W * 100).toFixed(1)}%`,
          transform: "translate(-50%, -115%)",
          background: "var(--bg-card)",
          border: "1px solid var(--lt-gold)",
          borderRadius: 8,
          padding: "6px 10px",
          fontSize: 11,
          whiteSpace: "nowrap",
          zIndex: 20,
          pointerEvents: "none",
          boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
        }}>
          <div style={{ fontWeight: 700, color: "var(--lt-gold)", marginBottom: 2 }}>
            {hoverData.label}
          </div>
          <div style={{ color: "var(--text-primary)" }}>
            평균 {hoverData.avg.toLocaleString()}만원/평
          </div>
          <div style={{ color: "var(--text-muted)" }}>
            {hoverData.count}건 거래
          </div>
        </div>
      )}
    </div>
  );
}

// ── 메인 섹션 ──────────────────────────────────────
export function LandTransactionSection({ sido, sigungu, isMobile }: Props) {
  const [chartData, setChartData] = useState<ChartPoint[]>([]);
  const [trades, setTrades] = useState<Trade[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showAll, setShowAll] = useState(false);

  const lawdCd = getLawdCd(sido, sigungu);

  const load = useCallback(async () => {
    if (!lawdCd) {
      setError(`${sigungu}의 법정동코드를 찾을 수 없습니다`);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE}/api/land/transactions?lawdCd=${lawdCd}&months=12`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = (await res.json()) as { chartData: ChartPoint[]; trades: Trade[] };
      setChartData(json.chartData);
      setTrades(json.trades);
    } catch (e: any) {
      setError(e.message || "데이터 조회 실패");
    } finally {
      setLoading(false);
    }
  }, [lawdCd, sigungu]);

  useEffect(() => { load(); }, [load]);

  if (!lawdCd) {
    return (
      <div style={st.section}>
        <h3 style={st.sectionTitle}>{"📊 주변 실거래 시세"}</h3>
        <div style={st.noData}>
          {`${sigungu}의 법정동코드 정보가 없습니다`}
        </div>
      </div>
    );
  }

  const displayTrades = showAll ? trades : trades.slice(0, 8);
  const totalCount = trades.reduce((s, _) => s + 1, 0);

  return (
    <div style={st.section}>
      {/* 섹션 헤더 */}
      <div style={st.sectionHead}>
        <h3 style={st.sectionTitle}>{"📊 주변 실거래 시세"}</h3>
        <span style={st.badge}>{sigungu}</span>
      </div>
      <p style={st.desc}>{"최근 12개월 토지 실거래 데이터 (국토교통부 공공데이터)"}</p>

      {/* 차트 영역 */}
      <div style={st.chartWrap}>
        {loading ? (
          <div style={st.loadBox}>
            <div style={st.spinner} />
            <span style={{ color: "var(--text-muted)", fontSize: 13 }}>{"데이터 조회 중..."}</span>
          </div>
        ) : error ? (
          <div style={st.errorBox}>
            <span style={{ color: "var(--lt-red)", fontSize: 13 }}>{error}</span>
            <button style={st.retryBtn} onClick={load}>{"다시 시도"}</button>
          </div>
        ) : (
          <>
            {/* Y축 레이블 */}
            <div style={{ fontSize: 10, color: "var(--text-muted)", marginBottom: 4, paddingLeft: 4 }}>
              {"단위: 만원/평"}
            </div>
            <PriceChart data={chartData} isMobile={isMobile} />
          </>
        )}
      </div>

      {/* 거래 목록 테이블 */}
      {!loading && !error && trades.length > 0 && (
        <div style={{ marginTop: 20 }}>
          <div style={st.tableHeader}>
            <span style={st.tableTitle}>{`최근 거래 내역 (총 ${totalCount}건)`}</span>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={st.table}>
              <thead>
                <tr>
                  {["거래일", "법정동", "지목", "면적(㎡)", "면적(평)", "금액(만원)", "평당가"].map((h) => (
                    <th key={h} style={st.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {displayTrades.map((t, i) => (
                  <tr key={i} style={{ background: i % 2 === 0 ? "transparent" : "rgba(255,255,255,0.02)" }}>
                    <td style={st.td}>{t.date}</td>
                    <td style={st.td}>{t.address}</td>
                    <td style={st.td}>{t.jimo}</td>
                    <td style={{ ...st.td, textAlign: "right" }}>{t.area.toLocaleString()}</td>
                    <td style={{ ...st.td, textAlign: "right" }}>{t.pyeong.toLocaleString()}</td>
                    <td style={{ ...st.td, textAlign: "right", color: "var(--text-primary)", fontWeight: 600 }}>
                      {formatPrice(t.price)}
                    </td>
                    <td style={{ ...st.td, textAlign: "right", color: "var(--lt-gold)", fontWeight: 700 }}>
                      {t.pricePerPyeong.toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {trades.length > 8 && (
            <button style={st.moreBtn} onClick={() => setShowAll(!showAll)}>
              {showAll ? "접기" : `더 보기 (${trades.length - 8}건 더)`}
            </button>
          )}
        </div>
      )}

      {!loading && !error && trades.length === 0 && (
        <div style={st.noData}>{"이 지역의 최근 12개월 토지 거래 내역이 없습니다"}</div>
      )}
    </div>
  );
}

// ── 스타일 ──────────────────────────────────────
const gold = "#D4A853";
const st: Record<string, React.CSSProperties> = {
  section: {
    background: "var(--bg-card)",
    border: "1px solid var(--border-light)",
    borderRadius: 12,
    padding: "20px 16px",
    marginBottom: 12,
  },
  sectionHead: {
    display: "flex", alignItems: "center", gap: 10, marginBottom: 4,
  },
  sectionTitle: {
    fontSize: 16, fontWeight: 700, color: "var(--text-primary)", margin: 0,
  },
  badge: {
    padding: "2px 8px", borderRadius: 20,
    background: `rgba(212,168,83,0.15)`, color: gold,
    fontSize: 12, fontWeight: 600,
  },
  desc: {
    fontSize: 12, color: "var(--text-muted)", margin: "0 0 14px",
  },
  chartWrap: {
    background: "var(--bg-secondary)", borderRadius: 10, padding: "12px 8px",
    minHeight: 120,
  },
  loadBox: {
    display: "flex", flexDirection: "column", alignItems: "center",
    justifyContent: "center", gap: 10, minHeight: 120,
  },
  spinner: {
    width: 28, height: 28, borderRadius: "50%",
    border: "3px solid var(--border-light)",
    borderTopColor: gold,
    animation: "spin 0.8s linear infinite",
  },
  errorBox: {
    display: "flex", flexDirection: "column", alignItems: "center", gap: 8,
    padding: 24,
  },
  retryBtn: {
    padding: "6px 16px", borderRadius: 8,
    border: `1px solid var(--border-medium)`,
    background: "transparent", color: "var(--text-secondary)",
    fontSize: 13, cursor: "pointer",
  },
  noData: {
    textAlign: "center" as const, color: "var(--text-muted)",
    fontSize: 13, padding: "24px 0",
  },
  tableHeader: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    marginBottom: 10,
  },
  tableTitle: {
    fontSize: 13, fontWeight: 600, color: "var(--text-secondary)",
  },
  table: {
    width: "100%", borderCollapse: "collapse" as const,
    fontSize: 12, minWidth: 540,
  },
  th: {
    padding: "7px 8px", textAlign: "left" as const,
    background: "var(--bg-secondary)", color: "var(--text-muted)",
    fontWeight: 600, borderBottom: "1px solid var(--border-light)",
    whiteSpace: "nowrap" as const,
  },
  td: {
    padding: "7px 8px", color: "var(--text-secondary)",
    borderBottom: "1px solid var(--border-light)", whiteSpace: "nowrap" as const,
  },
  moreBtn: {
    width: "100%", padding: "10px",
    border: `1px solid var(--border-medium)`,
    background: "transparent", borderRadius: 8,
    color: "var(--text-muted)", fontSize: 13, cursor: "pointer",
    marginTop: 8,
  },
};
